import React, { useState, useEffect } from 'react';
import { api, API, getToken } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Users, Briefcase, Coffee, Clock, MapPinOff, MapPin, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { ESTADO_LABEL, ESTADO_STYLE, ESTADO_DOT, formatHoras } from '../../lib/labels';
import { toast } from 'sonner';

const MOTIVO_LABEL = { fuera_de_zona: 'Fuera de zona', sin_ubicacion: 'Sin ubicación' };
const TIPO_LABEL = { entrada: 'Entrada', inicio_descanso: 'Inicio descanso', fin_descanso: 'Fin descanso', fin_jornada: 'Fin de jornada' };

const KpiCard = ({ icon: Icon, label, value, color, testid, onClick }) => (
  <Card className={`border-zinc-200 ${onClick ? 'cursor-pointer hover:border-zinc-400 transition-colors' : ''}`} data-testid={testid} onClick={onClick}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-sm text-zinc-500">{label}</p>
        <p className="font-heading text-2xl font-bold text-zinc-900 tabular-nums">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });

const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [gpsOpen, setGpsOpen] = useState(false);
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [incidencias, setIncidencias] = useState(null);

  useEffect(() => {
    const load = () => api.get('/admin/dashboard').then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const cargarIncidencias = async (desde = '', hasta = '') => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const r = await api.get('/admin/incidencias-gps', { params });
      setIncidencias(r.data);
    } catch (e) { toast.error('No se pudieron cargar las incidencias'); }
  };

  const abrirGps = () => {
    const h = hoyISO();
    const desde = new Date(Date.now() - 29 * 86400000).toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });
    setRango({ desde, hasta: h });
    setGpsOpen(true);
    cargarIncidencias(desde, h);
  };

  const descargar = (formato) => {
    const token = getToken();
    const params = new URLSearchParams({ formato, token });
    if (rango.desde) params.append('desde', rango.desde);
    if (rango.hasta) params.append('hasta', rango.hasta);
    window.open(`${API}/admin/incidencias-gps/export?${params.toString()}`, '_blank');
  };

  if (!data) return <p className="text-zinc-500">Cargando...</p>;

  const incHoy = data.incidencias_gps_hoy || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Empleados activos" value={data.total_empleados}
          color="bg-zinc-100 text-zinc-700" testid="kpi-total" />
        <KpiCard icon={Briefcase} label="Trabajando ahora" value={data.trabajando}
          color="bg-green-100 text-green-700" testid="kpi-trabajando" />
        <KpiCard icon={Coffee} label="En descanso" value={data.en_descanso}
          color="bg-amber-100 text-amber-700" testid="kpi-descanso" />
        <KpiCard icon={Clock} label="Horas hoy" value={formatHoras(data.horas_totales_hoy)}
          color="bg-blue-100 text-blue-700" testid="kpi-horas" />
      </div>

      {/* Aviso de fichajes fuera de zona (GPS) */}
      <button onClick={abrirGps} data-testid="gps-alert-card" className="w-full text-left">
        <Card className={`border ${incHoy > 0 ? 'border-red-300 bg-red-50' : 'border-zinc-200'} hover:shadow-sm transition-shadow`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${incHoy > 0 ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500'}`}>
              {incHoy > 0 ? <MapPinOff className="h-6 w-6" /> : <MapPin className="h-6 w-6" />}
            </div>
            <div className="flex-1">
              <p className={`font-semibold ${incHoy > 0 ? 'text-red-800' : 'text-zinc-800'}`}>
                {incHoy > 0 ? `${incHoy} fichaje(s) fuera de zona o sin ubicación hoy` : 'Sin incidencias de ubicación hoy'}
              </p>
              <p className="text-sm text-zinc-500">Fichajes desde la app del empleado con incidencia GPS. Pulsa para ver y descargar el detalle.</p>
            </div>
            <Download className="h-5 w-5 text-zinc-400" />
          </CardContent>
        </Card>
      </button>

      <Card className="border-zinc-200">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="font-heading font-semibold text-zinc-900">Estado del personal hoy</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  <th className="py-3 px-5">Empleado</th>
                  <th className="py-3 px-5">Estado</th>
                  <th className="py-3 px-5">Entrada</th>
                  <th className="py-3 px-5">Horas trabajadas</th>
                </tr>
              </thead>
              <tbody>
                {data.detalle.map((e, i) => (
                  <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-5 font-medium text-zinc-900">{e.full_name}</td>
                    <td className="py-3 px-5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${ESTADO_STYLE[e.estado]}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${ESTADO_DOT[e.estado]}`} />
                        {ESTADO_LABEL[e.estado]}
                      </span>
                    </td>
                    <td className="py-3 px-5 tabular-nums text-zinc-600">
                      {e.entrada ? new Date(e.entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '-'}
                    </td>
                    <td className="py-3 px-5 tabular-nums font-semibold text-zinc-900">{formatHoras(e.horas_trabajadas)}</td>
                  </tr>
                ))}
                {data.detalle.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-zinc-400">No hay empleados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Diálogo de incidencias GPS */}
      <Dialog open={gpsOpen} onOpenChange={setGpsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gps-incidencias-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPinOff className="h-5 w-5" /> Incidencias de fichaje por GPS</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Desde</Label>
              <Input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="gps-desde" className="w-40" /></div>
            <div className="space-y-1"><Label className="text-xs">Hasta</Label>
              <Input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="gps-hasta" className="w-40" /></div>
            <Button variant="outline" size="sm" onClick={() => cargarIncidencias(rango.desde, rango.hasta)} data-testid="gps-buscar-btn">Buscar</Button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={() => descargar('pdf')} data-testid="gps-export-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => descargar('excel')} data-testid="gps-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
          </div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden mt-2">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                <tr>
                  <th className="py-2 px-3 text-left">Empleado</th>
                  <th className="py-2 px-3 text-left">Fecha/Hora</th>
                  <th className="py-2 px-3 text-left">Fichaje</th>
                  <th className="py-2 px-3 text-left">Centro</th>
                  <th className="py-2 px-3 text-left">Motivo</th>
                  <th className="py-2 px-3 text-right">Distancia</th>
                </tr>
              </thead>
              <tbody data-testid="gps-incidencias-tabla">
                {(incidencias?.items || []).map((it) => (
                  <tr key={it.id} className="border-t border-zinc-100">
                    <td className="py-2 px-3 font-medium text-zinc-800">{it.full_name || it.username}</td>
                    <td className="py-2 px-3 tabular-nums text-zinc-600">{new Date(it.timestamp).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="py-2 px-3 text-zinc-600">{TIPO_LABEL[it.tipo] || it.tipo}</td>
                    <td className="py-2 px-3 text-zinc-600">{it.centro || '-'}</td>
                    <td className="py-2 px-3">
                      <Badge className={it.gps_motivo === 'fuera_de_zona' ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}>
                        {MOTIVO_LABEL[it.gps_motivo] || it.gps_motivo}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-zinc-600">{it.distancia_m != null ? `${Math.round(it.distancia_m)} m` : '-'}</td>
                  </tr>
                ))}
                {incidencias && incidencias.items.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-zinc-400">Sin incidencias en el periodo seleccionado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardTab;
